import { KEEP, tablesOf, tableRows, rowText, mapParagraphs } from "./exporters/ooxml.js";
import { isOpenRisk, isScored, riskScore, highRiskThreshold, isRatedHigh } from "./calc.js";
import { daysFrom } from "./format.js";

/* The monthly deck, as instructions for the template filler.
 *
 * buildReport() in report.js works out what the month's numbers are. This
 * works out where each one goes in the client's own template, and nothing
 * else: no arithmetic about invoices or deliverables happens here that the
 * Reports page does not already show. The two cannot disagree, because the
 * deck is built from the same report object the page draws.
 *
 * Slides are found by their titles in the template, never by their position,
 * so a template with a slide added, removed or dragged still fills. Titles
 * are matched against the template, not against any filled deck: the July
 * draft says "Function Module" and "Pole Installation Progress", and the
 * template, which is what gets filled, says neither.
 *
 * What a person writes is left as the template's own placeholder, so it shows
 * on the unfilled list and on the slide, rather than being filled with
 * something that sounds plausible.
 */

/* ------------------------------------------------------------ formatting */

/* Every date in the deck goes through one of these, which is the cure for
   4/24/26 sitting beside 10/17/2025 on one table. */
const parts = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const mdyy = (iso) => {
  const p = parts(iso);
  return p ? `${p.m}/${p.d}/${String(p.y).slice(2)}` : "";
};
export const mmddyy = (iso) => {
  const p = parts(iso);
  return p ? `${String(p.m).padStart(2, "0")}/${String(p.d).padStart(2, "0")}/${String(p.y).slice(2)}` : "";
};
export const md = (iso) => {
  const p = parts(iso);
  return p ? `${p.m}/${p.d}` : "";
};
export const monD = (iso) => {
  const p = parts(iso);
  return p ? `${MON[p.m - 1]} ${p.d}` : "";
};
export const monYYYY = (iso) => {
  const p = parts(iso);
  return p ? `${MON[p.m - 1]} ${p.y}` : "";
};
export const monDYYYY = (iso) => {
  const p = parts(iso);
  return p ? `${MON[p.m - 1]} ${p.d}, ${p.y}` : "";
};
export const money = (n) => (Number(n) || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const pct1 = (part, whole) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : "0.0%");
const pct0 = (n) => `${Math.round(Number(n) || 0)}%`;

/* Names compared the way a person reads them: case, ampersands, the word
   "stage" and punctuation do not make two names different. */
const STOP = new Set(["stage", "and", "the", "phase", "of"]);
export const words = (s) => String(s ?? "").toLowerCase()
  .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim().split(" ")
  .filter((w) => w && !STOP.has(w));
const sameWords = (a, b) => {
  const x = words(a);
  const y = words(b);
  if (!x.length || !y.length) return false;
  const [small, big] = x.length <= y.length ? [x, y] : [y, x];
  return small.every((w) => big.includes(w));
};

/* ------------------------------------------------------- the deck's colours */

/* Excel's own Good, Neutral and Bad styles, because the status tables began
   life in Excel and the deck's readers know those boxes on sight. */
const SWATCH = {
  green: { fill: "C6EFCE", ink: "006100" },
  amber: { fill: "FFEB9C", ink: "9C5700" },
  red: { fill: "FFC7CE", ink: "9C0006" },
  grey: { fill: "F2F2F2", ink: "595959" },
};

const STATUS_SWATCH = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (/on track|complete/.test(s)) return SWATCH.green;
  if (/at risk|monitor|off track/.test(s)) return SWATCH.amber;
  if (/critical|behind|late/.test(s)) return SWATCH.red;
  if (/not started/.test(s)) return SWATCH.grey;
  return null;
};
const painted = (text, swatch) => (swatch ? { text, ...swatch } : text);

/* ------------------------------------------------------ reading a template */

/* Each slide the deck fills, by the words in its title. The title is the
   template's, and only as much of it as is needed to tell slides apart. */
export const SLIDE_TITLES = {
  cover: "Monthly Status Report",
  status: "Overall Project Status",
  fm: "Functional Module Testing",
  install: "Installation Progress",
  tracker: "Contract Deliverables Tracker",
  overdue: "Overdue Contract Deliverables",
  risks: "Key Risks / Issues",
  schedule: "Project Schedule Overview",
  beyond: "Aging Beyond Payment Terms",
  within: "Aging Within Terms",
  trend: "Month Over Month Trend",
};

function texts(xml) {
  const out = [];
  mapParagraphs(xml, (t) => { out.push(t); return t; });
  return out;
}

/** Which template slide holds which part of the report, plus what each one's
 *  tables look like: how many body rows were drawn, which is how many the
 *  slide was designed to hold, and the text of each row. */
export function readTemplate(slides = []) {
  const found = {};
  for (const { slide, xml } of slides) {
    const lines = texts(xml);
    for (const [role, title] of Object.entries(SLIDE_TITLES)) {
      if (found[role]) continue;
      if (!lines.some((t) => t.includes(title))) continue;
      found[role] = {
        slide,
        title: lines.find((t) => t.includes(title)),
        tables: tablesOf(xml).map((tbl) => {
          const rows = tableRows(tbl).map(rowText);
          return { rows, body: Math.max(0, rows.length - 1) };
        }),
        lines,
      };
    }
  }
  return found;
}

/* Splits rows into pages of the size the template drew. */
export function paginate(rows, size) {
  if (!rows.length) return [[]];
  const per = Math.max(1, size || rows.length);
  const pages = [];
  for (let i = 0; i < rows.length; i += per) pages.push(rows.slice(i, i + per));
  return pages;
}

/* One page per chunk, the first being the template slide itself. Every later
   one carries "(continued)" on its title, which is what July's second aging
   slide should have said and did not. */
function pagesFor(found, chunks, build) {
  return chunks.map((chunk, i) => {
    const page = build(chunk, i);
    if (i > 0) {
      page.paragraphs = [...(page.paragraphs ?? []),
        { startsWith: found.title, text: `${found.title} (continued)` }];
    }
    return page;
  });
}

/* --------------------------------------------------------------- trackers */

const column = (tracker, pattern) =>
  (tracker?.columns ?? []).find((c) => pattern.test(String(c.label ?? c.key ?? "")))?.key;

const isTrue = (v) => v === true || v === 1 || /^(1|yes|true|y)$/i.test(String(v ?? "").trim());

/** Trackers whose names fit a slide, best first. A tie is returned as a tie:
 *  the screen asks rather than this guessing between two install trackers. */
export function trackerCandidates(trackers = []) {
  const installs = trackers.filter((t) => /install/i.test(t.title ?? ""));
  const fm = trackers.filter((t) => /\bfm\b|functional|module|testing/i.test(t.title ?? ""));
  return { install: installs, fm };
}

/* What the tracker calls the thing being installed, from its own name less
   the words every tracker of this kind carries and the contract's own name.
   "WAPA Pole Install" on the WAPA contract is poles. A person can overrule it
   on screen, and anything odd is theirs to fix there rather than ours to
   second guess here. */
export function assetFromTitle(title, contract) {
  const skip = new Set(["install", "installs", "installation", "tracker", "progress", "site", "sites",
                        "list", "log", ...words(contract?.name), ...words(contract?.client)]);
  const left = String(title ?? "").split(/\s+/).filter((w) => w && !skip.has(w.toLowerCase().replace(/[^a-z0-9]/g, "")));
  if (!left.length) return "";
  const last = left[left.length - 1];
  left[left.length - 1] = /s$/i.test(last) ? last : `${last}s`;
  return left.join(" ");
}

/**
 * Installation progress by island, as at the end of the month.
 *
 * Installed means a completion date on or before month end. A row marked
 * complete with no date at all still counts, the same rule the deliverables
 * tracker uses, because refusing to count finished work understates every
 * month. A row the tracker marks out of scope (a scope column reading 0) is
 * left out entirely, which is how the 125 sites on the sheet are the 124 the
 * July deck reports.
 */
export function installProgress(tracker, rows = [], { start, end } = {}) {
  const island = column(tracker, /island|region/i);
  const done = column(tracker, /date\s*complet|complet\w*\s*date|install\w*\s*date/i);
  const status = column(tracker, /^status$|install\w* status|status/i);
  const scope = column(tracker, /scope/i);
  const week = column(tracker, /week\s*ending/i);
  if (!island || !done) return null;

  const inScope = rows.map((r) => r.data ?? r).filter((d) => !scope || d[scope] === undefined
    || d[scope] === null || d[scope] === "" || isTrue(d[scope]));
  const completedOn = (d) => {
    const date = String(d[done] ?? "").slice(0, 10);
    if (date) return date <= end ? date : null;
    return /complete/i.test(String(d[status] ?? "")) ? "undated" : null;
  };

  const groups = new Map();
  for (const d of inScope) {
    const key = String(d[island] ?? "").trim() || "Unassigned";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(d);
  }

  const summarise = (list) => {
    const dates = list.map(completedOn);
    const complete = dates.filter(Boolean).length;
    const period = list.filter((d) => {
      const date = String(d[done] ?? "").slice(0, 10);
      return date && date >= start && date <= end;
    });
    const periodDates = period.map((d) => String(d[done]).slice(0, 10)).sort();
    const real = dates.filter((x) => x && x !== "undated").sort();
    return {
      total: list.length,
      complete,
      remaining: list.length - complete,
      thisPeriod: period.length,
      firstInPeriod: periodDates[0] ?? null,
      lastInPeriod: periodDates.at(-1) ?? null,
      lastComplete: real.at(-1) ?? null,
    };
  };

  const weekEnding = week
    ? inScope.map((d) => String(d[week] ?? "").slice(0, 10)).filter((x) => x && x <= end).sort().at(-1) ?? null
    : null;

  return {
    islands: [...groups].map(([name, list]) => ({ name, ...summarise(list) })),
    all: summarise(inScope),
    weekEnding,
    excluded: rows.length - inScope.length,
  };
}

/* The functional module dashboard, from a tracker with a row per module.
   Columns are found by name: the module, its TID status, Build & UT and SIT
   percentages, and an overall status. */
export function fmTesting(tracker, rows = []) {
  const name = column(tracker, /^fm$|module|^name$|^title$/i);
  const tid = column(tracker, /\btid\b/i);
  const build = column(tracker, /build/i);
  const sit = column(tracker, /\bsit\b/i);
  const status = column(tracker, /status/i);
  if (!name || !tid || !build || !sit) return null;
  const pctOf = (v) => {
    const n = Number(String(v ?? "").replace("%", ""));
    if (!Number.isFinite(n)) return 0;
    return n <= 1 && !/%/.test(String(v)) ? n * 100 : n;
  };
  const modules = rows.map((r) => r.data ?? r).map((d) => ({
    name: String(d[name] ?? ""),
    tid: String(d[tid] ?? ""),
    build: pctOf(d[build]),
    sit: pctOf(d[sit]),
    status: status && status !== tid ? String(d[status] ?? "") : "",
  }));
  const avg = (key) => (modules.length ? modules.reduce((s, m) => s + m[key], 0) / modules.length : 0);
  const tidDone = modules.filter((m) => /complete/i.test(m.tid)).length;
  return { modules, tidDone, total: modules.length, build: avg("build"), sit: avg("sit") };
}

/* --------------------------------------------------------------- schedule */

const symbol = (pct, done) => (done || pct >= 100 ? "✓" : pct > 0 ? "●" : "○");

/**
 * The schedule overview, from the schedule of record as it stands. A schedule
 * row has no history, the same as a risk, so this is today's plan whatever
 * month is chosen, and the screen says so.
 *
 * Phase boxes are the template's six, matched to the plan's summary rows by
 * name and only by name. A box with no row of the same name stays a
 * placeholder rather than borrowing the nearest-sounding phase, because a
 * client slide showing the wrong phase's dates is worse than one that is
 * visibly unfinished.
 */
export function scheduleOverview(activities = [], { ref, phaseNames = [] } = {}) {
  const live = activities.filter((a) => !a.deleted_at);
  if (!live.length) return null;
  const shallowest = Math.min(...live.map((a) => Number(a.outline_level ?? 0)));
  const roots = live.filter((a) => Number(a.outline_level ?? 0) === shallowest);
  const root = roots.length === 1 ? roots[0] : null;

  const summaries = live.filter((a) => a.is_summary && a !== root)
    .sort((a, b) => Number(a.outline_level ?? 0) - Number(b.outline_level ?? 0));
  const phases = phaseNames.map((label) => {
    const row = summaries.find((a) => sameWords(label, a.title));
    return row
      ? { label, row, start: row.start_date, finish: row.finish_date, pct: Number(row.percent_complete) || 0 }
      : { label, row: null };
  });

  const isDone = (a) => Number(a.percent_complete) >= 100 || a.finish_is_actual === true;
  const milestones = live.filter((a) => a.is_milestone && !a.is_summary && a.finish_date)
    .sort((a, b) => String(a.finish_date).localeCompare(String(b.finish_date)));
  /* The last two reached and then what comes next, five in all, which is the
     shape of the template's list: where we have been and where we are going. */
  const reached = milestones.filter((a) => isDone(a) && a.finish_date <= ref).slice(-2);
  const ahead = milestones.filter((a) => !reached.includes(a) && !isDone(a)).slice(0, 5 - reached.length);
  const keyMilestones = [...reached, ...ahead].map((a) => ({
    title: a.title,
    date: a.finish_date,
    done: isDone(a),
    pct: Number(a.percent_complete) || 0,
  }));

  const critical = live.filter((a) => a.is_critical && !a.is_summary && !a.is_milestone && !isDone(a))
    .sort((a, b) => String(a.early_start ?? a.start_date ?? "").localeCompare(String(b.early_start ?? b.start_date ?? "")))
    .slice(0, 4)
    .map((a) => {
      const pct = Number(a.percent_complete) || 0;
      const late = (a.finish_date && a.finish_date < ref) || (a.start_date && a.start_date < ref && pct === 0);
      return { title: a.title, pct, status: late ? "Behind Schedule" : pct > 0 ? "In Progress" : "Not Started" };
    });

  return {
    overall: root ? {
      pct: Number(root.percent_complete) || 0, start: root.start_date, finish: root.finish_date,
    } : null,
    phases,
    milestones: keyMilestones,
    critical,
  };
}

/* ---------------------------------------------------- risks and issues */

const label = (v) => {
  const s = String(v ?? "").trim();
  if (/^h/i.test(s)) return "High";
  if (/^m/i.test(s)) return "Medium";
  if (/^l/i.test(s)) return "Low";
  return s;
};

/**
 * The key risks and issues slide: open items rated High or Medium, which is
 * what the client has been receiving every month. High before Medium, and
 * within each, issues (already happening) before risks (might), worst score
 * first. Every one is shown, onto further slides if need be, because cutting
 * the list at the slide's six rows would be choosing which items WAPA hears
 * about by the size of a table.
 *
 * A scored risk is High at or over the contract's own threshold and Medium at
 * or over half of it, so the bands move with the contract's scale: 15 and 8
 * on WAPA's five by five, 9 and 5 on VIDE's three by four. A risk with a
 * rating and no score is judged on its rating, as everywhere else.
 */
export function keyRisksAndIssues(risks = [], issues = [], contract) {
  const openIssues = issues.filter((i) => !/closed|resolved/i.test(String(i.status ?? "")))
    .map((i) => ({
      title: i.title, type: "Issue", impact: label(i.impact), owner: i.owner ?? "",
      plan: i.mitigation || i.latest_update || "", score: 0,
    }));
  const threshold = highRiskThreshold(contract);
  const band = (r) => {
    if (isScored(r)) {
      const s = riskScore(r);
      return s >= threshold ? "High" : s >= Math.ceil(threshold / 2) ? "Medium" : "Low";
    }
    if (isRatedHigh(r)) return "High";
    return label(r.rating) || label(r.impact_label) || "";
  };
  const openRisks = risks.filter(isOpenRisk).map((r) => ({
    title: r.title, type: "Risk", impact: band(r),
    owner: r.owner ?? "", plan: r.mitigation || "", score: isScored(r) ? riskScore(r) : 0,
  }));
  const rank = { High: 0, Medium: 1 };
  return [...openIssues, ...openRisks]
    .filter((i) => i.impact in rank)
    .sort((a, b) => rank[a.impact] - rank[b.impact]
      || (a.type === b.type ? 0 : a.type === "Issue" ? -1 : 1)
      || b.score - a.score)
    .map(({ score, ...item }) => item);
}

/* ------------------------------------------------------------- the plan */

const shortStage = (s) => String(s ?? "").replace(/\s+stage$/i, "").trim() || "No stage";

/**
 * report     buildReport()'s output for the month
 * template   readTemplate() of the uploaded deck
 * extras     what the report object does not carry:
 *              risks, issues            the registers, for slide 14
 *              schedule                 activities from the schedule engine
 *              install { tracker, rows, asset }    the confirmed slide 9 tracker
 *              fm      { tracker, rows }           the confirmed slide 7 tracker
 *
 * returns { plan, notes }. notes are things a person should know about how
 * the deck was filled that are not unfilled placeholders: a stage with no
 * column, a phase with no match, a tracker not yet chosen.
 */
export function planDeck({ report, contract, template, extras = {} }) {
  const notes = [];
  const slides = {};
  const { start, end } = report.window;

  /* ---- slide 4: overall status */
  const status = template.status;
  if (status) {
    const rows = report.workstreams.map((w) => [
      w.title, painted(w.status || "", STATUS_SWATCH(w.status)), w.comment || "",
    ]);
    if (rows.length) {
      const count = (re) => report.workstreams.filter((w) => re.test(String(w.status ?? ""))).length;
      const kpis = [/on track/i, /at risk|off track/i, /critical/i, /not started/i].map((re) => String(count(re)));
      slides[status.slide] = pagesFor(status, paginate(rows, status.tables[0]?.body), (chunk) => ({
        tables: [{ index: 0, rows: chunk }],
        values: { "[#]": kpis },
      }));
      if (report.workstreams.some((w) => w.undated)) {
        notes.push(`Some ${String(contract?.workstream_label || "workstream").toLowerCase()} statuses have no month set, so they appear in every month's deck.`);
      }
    }
  }

  /* ---- slide 7: functional module testing */
  const fmSlide = template.fm;
  if (fmSlide) {
    const fm = extras.fm?.tracker ? fmTesting(extras.fm.tracker, extras.fm.rows) : null;
    if (fm && fm.total) {
      const rows = fm.modules.map((m) => [
        m.name, painted(m.tid, STATUS_SWATCH(m.tid)), pct0(m.build), pct0(m.sit),
        painted(m.status || (m.sit >= 100 ? "Completed" : m.build > 0 || m.sit > 0 ? "In Progress" : "Not Started"),
          STATUS_SWATCH(m.status || (m.sit >= 100 ? "complete" : ""))),
      ]);
      slides[fmSlide.slide] = pagesFor(fmSlide, paginate(rows, fmSlide.tables[0]?.body), (chunk) => ({
        tables: [{ index: 0, rows: chunk }],
        values: {
          "[##%]": [pct0((fm.tidDone / fm.total) * 100), pct0(fm.build), pct0(fm.sit)],
          "[## / 14 done]": `${fm.tidDone} / ${fm.total} done`,
        },
      }));
    } else {
      notes.push(extras.fm?.tracker
        ? `The ${extras.fm.tracker.title} tracker has no FM, TID, Build and SIT columns, so the testing dashboard is left for you.`
        : "No functional module testing tracker is chosen, so the testing dashboard is left for you.");
    }
  }

  /* ---- slide 9: installation progress */
  const install = template.install;
  if (install) {
    const progress = extras.install?.tracker
      ? installProgress(extras.install.tracker, extras.install.rows, { start, end })
      : null;
    if (progress) {
      const t = install.tables[0];
      const templateNames = (t?.rows ?? []).slice(1).map((r) => r[0]);
      /* The template's islands in its order, then any others the tracker has.
         An island with no rows gets no row, which is the template's own note
         about Water Island done for it. */
      const known = templateNames.filter((n) => progress.islands.some((i) => sameWords(i.name, n)));
      const ordered = [
        ...known.map((n) => progress.islands.find((i) => sameWords(i.name, n))),
        ...progress.islands.filter((i) => !known.some((n) => sameWords(i.name, n))),
      ];
      const estimate = (s) => (s.remaining === 0 && s.lastComplete
        ? `Complete ${md(s.lastComplete)}`
        : KEEP);
      const line = (name, s) => [
        name, String(s.total), String(s.complete), String(s.remaining),
        pct1(s.complete, s.total), String(s.thisPeriod), estimate(s),
      ];
      const totalLabel = templateNames.at(-1) || "All";
      const rows = [
        ...ordered.map((s) => ({ like: 0, cells: line(s.name, s) })),
        { like: Math.max(0, (t?.body ?? 1) - 1), cells: line(totalLabel, progress.all) },
      ];
      const a = progress.all;
      const asset = extras.install.asset || assetFromTitle(extras.install.tracker.title, contract);
      slides[install.slide] = {
        tables: [{ index: 0, rows }],
        values: {
          "[Asset Type]": asset || "[Asset Type]",
          "[Month YYYY]": report.label,
          "[Report name]": extras.install.tracker.source_file
            ? String(extras.install.tracker.source_file).replace(/\.[a-z0-9]+$/i, "")
            : extras.install.tracker.title,
          "[MM/DD/YY]": progress.weekEnding ? mmddyy(progress.weekEnding) : "[MM/DD/YY]",
          "[###]": [String(a.complete), String(a.total)],
          "[##%]": pct1(a.complete, a.total),
          "[##]": String(a.thisPeriod),
          "[Mon D]": a.thisPeriod ? [monD(a.firstInPeriod), monD(a.lastInPeriod)] : [],
        },
      };
      if (progress.excluded) {
        notes.push(`${progress.excluded} row${progress.excluded === 1 ? " is" : "s are"} marked out of scope on ${extras.install.tracker.title} and left out of the installation figures.`);
      }
    } else {
      notes.push(extras.install?.tracker
        ? `The ${extras.install.tracker.title} tracker has no island and completion date columns, so installation progress is left for you.`
        : "No installation tracker is chosen, so installation progress is left for you.");
    }
  }

  /* ---- slide 12: contract deliverables tracker */
  const tracker = template.tracker;
  if (tracker && report.tracker.program.total) {
    const t = report.tracker;
    const shape = tracker.tables[0];
    const header = shape?.rows[0] ?? [];
    const stageCols = header.slice(1, -1);
    /* Which of the report's stages sits under each of the template's columns.
       A stage with no column still counts in the totals, as July's 188 did,
       and is named in the notes so the difference is not a mystery. */
    const colStage = stageCols.map((h) => t.stages.findIndex((s) => sameWords(h, s)));
    const unplaced = t.stages.filter((_, i) => !colStage.includes(i));
    for (const s of unplaced) {
      const n = t.rows.concat(t.clientRows).reduce((sum, r) => sum + (r.cells[t.stages.indexOf(s)]?.total ?? 0), 0);
      if (n) notes.push(`${n} deliverable${n === 1 ? " in the stage \"" + s + "\" has" : "s in the stage \"" + s + "\" have"} no column on the tracker slide. ${n === 1 ? "It is" : "They are"} counted in the totals.`);
    }
    stageCols.forEach((h, i) => {
      if (colStage[i] === -1) notes.push(`No stage matches the tracker slide's "${h}" column, so it shows a dash throughout.`);
    });

    const box = (c) => (!c || c.state === "none" ? "-" : painted(`${c.complete} / ${c.total}`, SWATCH[c.state]));
    const cellsFor = (cells) => colStage.map((i) => (i === -1 ? "-" : box(cells[i])));
    const sums = (cells) => colStage.map((i) => (i === -1 ? "-" : `${cells[i].complete} / ${cells[i].total}`));
    const labels = (shape?.rows ?? []).map((r) => r[0]);
    const body = shape?.body ?? 0;
    /* The template's own rows below the vendors, found by their labels so a
       template that drops the client row still lines up. */
    const findRow = (re) => labels.slice(1).findIndex((l) => re.test(l));
    const subtotalAt = findRow(/subtotal/i);
    const clientAt = findRow(/client/i);
    const totalAt = findRow(/total deliverables/i);
    const completeAt = findRow(/total complete/i);

    /* Vendors in the order the template lists them, which is the order the
       client has read them in every month, then any the template does not
       name, alphabetically. */
    const named = labels.slice(1, subtotalAt >= 0 ? subtotalAt + 1 : undefined);
    const rank = (v) => {
      const at = named.findIndex((l) => sameWords(l, v));
      return at === -1 ? named.length : at;
    };
    const vendorRows = [...t.rows].sort((a, b) => rank(a.vendor) - rank(b.vendor)
      || a.vendor.localeCompare(b.vendor));
    const rows = [
      ...vendorRows.map((r) => ({ like: 0, cells: [r.vendor, ...cellsFor(r.cells), `${r.complete} / ${r.total}`] })),
    ];
    if (subtotalAt >= 0) {
      rows.push({ like: subtotalAt, cells: [KEEP, ...sums(t.vendorSubtotal.cells),
        `${t.vendorSubtotal.complete} / ${t.vendorSubtotal.total}`] });
    }
    for (const r of t.clientRows) {
      rows.push({ like: clientAt >= 0 ? clientAt : 0,
        cells: [r.vendor, ...cellsFor(r.cells), `${r.complete} / ${r.total}`] });
    }
    if (totalAt >= 0) {
      rows.push({ like: totalAt, cells: [KEEP,
        ...colStage.map((i) => (i === -1 ? "-" : String(t.program.cells[i].total))), String(t.program.total)] });
    }
    if (completeAt >= 0) {
      rows.push({ like: completeAt, cells: [KEEP,
        ...colStage.map((i) => (i === -1 ? "-" : String(t.program.cells[i].complete))), String(t.program.complete)] });
    }
    slides[tracker.slide] = { tables: [{ index: 0, rows }] };
    if (rows.length > body) {
      notes.push(`The tracker table has ${rows.length} rows where the template drew ${body}. Check it still sits clear of the legend.`);
    }
    if (t.undated) {
      notes.push(`${t.undated} deliverable${t.undated === 1 ? " is" : "s are"} marked complete with no completion date, so ${t.undated === 1 ? "it counts" : "they count"} as complete in every month.`);
    }
  }

  /* ---- slide 13: overdue contract deliverables */
  const overdue = template.overdue;
  if (overdue) {
    const why = overdue.tables[0]?.rows[1]?.[3] ?? "";
    const rows = report.overdue.map((r) => {
      const reasons = r.stages.map((s) => {
        const said = [...new Set(s.items.map((d) => String(d.at_risk_note ?? "").trim()).filter(Boolean))];
        return said.length ? `${shortStage(s.stage)}: ${said.join("; ")}` : null;
      }).filter(Boolean);
      return [
        r.vendor,
        String(r.count),
        r.stages.map((s) => `${shortStage(s.stage)} (${s.items.length}): ${s.items.map((d) => d.title).join(", ")}`).join("\n"),
        reasons.length ? reasons.join("\n") : (why ? KEEP : ""),
      ];
    });
    if (rows.length) {
      slides[overdue.slide] = pagesFor(overdue, paginate(rows, overdue.tables[0]?.body), (chunk) => ({
        tables: [{ index: 0, rows: chunk }],
      }));
    } else {
      slides[overdue.slide] = {
        tables: [{ index: 0, rows: [["None", "0", "Nothing was overdue at month end.", ""]] }],
      };
    }
  }

  /* ---- slide 14: key risks and issues */
  const riskSlide = template.risks;
  if (riskSlide) {
    const items = keyRisksAndIssues(extras.risks ?? [], extras.issues ?? [], contract);
    const rows = items.map((i) => [i.title, i.type, painted(i.impact, i.impact === "High" ? SWATCH.red
      : i.impact === "Medium" ? SWATCH.amber : null), i.owner, i.plan]);
    if (rows.length) {
      slides[riskSlide.slide] = pagesFor(riskSlide, paginate(rows, riskSlide.tables[0]?.body), (chunk) => ({
        tables: [{ index: 0, rows: chunk }],
      }));
    }
  }

  /* ---- slide 15: schedule overview */
  const sched = template.schedule;
  if (sched) {
    /* The phase boxes' labels are the lines that come before each box's
       dates, in the template's own order. */
    const phaseNames = sched.lines
      .map((t, i) => (sched.lines[i + 1] === "[Mon YYYY] to [Mon YYYY]" ? t : null))
      .filter((t) => t && !/^PROJECT PHASE SUMMARY/.test(t));
    const ov = scheduleOverview(extras.schedule ?? [], { ref: end, phaseNames });
    if (ov) {
      const months = [];
      const marks = [];
      const percents = [];
      if (ov.overall) months.push(monYYYY(ov.overall.start), monYYYY(ov.overall.finish));
      else months.push("[Mon YYYY]", "[Mon YYYY]");
      for (const p of ov.phases) {
        if (p.row) {
          months.push(monYYYY(p.start), monYYYY(p.finish));
          marks.push(symbol(p.pct));
          percents.push(pct0(p.pct));
        } else {
          months.push("[Mon YYYY]", "[Mon YYYY]");
          marks.push("[✓ / ● / ○]");
          percents.push("[##%]");
          notes.push(`No schedule phase is named like "${p.label}", so that box on the schedule slide is left for you.`);
        }
      }
      const lists = [];
      if (ov.milestones.length) {
        lists.push({
          anchor: "[Milestone]",
          lines: ov.milestones.map((m) => `${symbol(m.pct, m.done)}  ${m.title}  |  ${monDYYYY(m.date)} (${m.done ? "Complete" : "Target"})`),
        });
      }
      if (ov.critical.length) {
        lists.push({
          anchor: "[Item]",
          lines: ov.critical.map((c) => `${c.title}  |  ${pct0(c.pct)}  |  ${c.status}: [cause]`),
        });
      }
      slides[sched.slide] = {
        lists,
        values: {
          "[##]": ov.overall ? String(Math.round(ov.overall.pct)) : "[##]",
          "[Mon YYYY]": months,
          "[✓ / ● / ○]": marks,
          "[##%]": percents,
        },
      };
    }
  }

  /* ---- slides 16 and 17: invoices */
  const ageOf = (p) => `${Math.max(0, daysFrom(p.invoice.submitted, report.asOf))} days`;
  const invoiceRow = (p) => [
    p.invoice.vendor || "", p.invoice.title || "", mdyy(p.invoice.submitted),
    `$${money(p.invoice.amount)}`, p.invoice.submitted ? ageOf(p) : "", p.invoice.description || "",
  ];
  const byVendorThenDate = (a, b) => String(a.invoice.vendor ?? "").localeCompare(String(b.invoice.vendor ?? ""))
    || String(a.invoice.submitted ?? "").localeCompare(String(b.invoice.submitted ?? ""));
  const inv = report.invoices;

  const beyond = template.beyond;
  if (beyond) {
    const items = [...inv.beyondTerms.items].sort(byVendorThenDate);
    /* The footnote is about a vendor whose terms are not the contract's, which
       is why the deck has one: AWG is on 45 while everybody else is on 60. */
    const unusual = [...new Map(items
      .filter((p) => p.aging.termsDays && p.aging.termsDays !== Number(contract?.payment_terms_days || 60))
      .map((p) => [p.invoice.vendor, p.aging.termsDays])).entries()];
    const footnote = unusual.length === 1
      ? { startsWith: "*Per ", text: `*Per ${unusual[0][0]}'s contract, aging falls at risk beyond the ${unusual[0][1]}-day mark.` }
      : unusual.length > 1
        ? { startsWith: "*Per ", text: `*Per their contracts, aging falls at risk beyond ${unusual.map(([v, d]) => `${d} days for ${v}`).join(" and ")}.` }
        : { startsWith: "*Per ", text: null };
    const rows = items.map(invoiceRow);
    const banner = items.length ? [] : [{
      startsWith: "URGENT:", text: `No invoices were aged beyond payment terms at the end of ${report.label}.`,
    }];
    slides[beyond.slide] = pagesFor(beyond, paginate(rows, beyond.tables[0]?.body), (chunk) => ({
      tables: [{ index: 0, rows: chunk.length ? chunk : [["None", "", "", "", "", ""]] }],
      paragraphs: [footnote, ...banner],
      values: {
        "[##]": String(inv.beyondTerms.count),
        "[#,###,###.##]": money(inv.beyondTerms.dollars),
      },
    }));
  }

  const within = template.within;
  if (within) {
    const items = [...inv.withinTerms.items].sort(byVendorThenDate);
    const rows = items.map(invoiceRow);
    const count = inv.withinTerms.count + inv.beyondTerms.count;
    /* The summary is the whole aging picture and the recommendations are
       about all of it, so both go once, on the last page, as they did in
       July. A copy on every page reads as that page's own totals. */
    const lastOnly = [
      within.lines.find((t) => /aging summary/i.test(t)),
      within.lines.find((t) => /^consulting recommendations?$/i.test(t.trim())),
    ].filter(Boolean);
    const chunks = paginate(rows, within.tables[0]?.body);
    slides[within.slide] = pagesFor(within, chunks, (chunk, i) => ({
      remove: i < chunks.length - 1 ? lastOnly : [],
      tables: [{ index: 0, rows: chunk.length ? chunk : [["None", "", "", "", "", ""]] }],
      values: {
        "[#,###,###.##]": [money(inv.withinTerms.dollars), money(inv.beyondTerms.dollars), money(inv.totalOutstanding)],
        "[#]": [String(inv.withinTerms.count), String(inv.beyondTerms.count), String(count)],
      },
    }));
  }

  /* Slide 18, the six month trend, is left for the step that freezes a month
     at issue. Its month label still fills, from the deck-wide values. */

  return {
    plan: {
      values: { "[Month Year]": report.label },
      slides,
      draft: { text: "DRAFT" },
    },
    notes,
  };
}
